<script setup lang="ts">
import { computed, shallowRef, watch } from 'vue'
import { formatNumber } from '@/utils/number'
import { buildImageResizeUrl } from '@/utils/distribute/media-url'
import type { Props } from './type'

defineOptions({
  name: 'CartoonProfileHeader',
})

const props = withDefaults(defineProps<Props>(), {
  followLoading: false,
  showFollowButton: true,
})

const emit = defineEmits<{
  followClick: []
  authorClick: []
}>()

const avatarSrc = computed(
  () =>
    buildImageResizeUrl(props.profile.avatar, { width: 280, height: 280, mode: 'fill' }) ??
    props.profile.avatar,
)
const summaryText = computed(() => props.profile.summary?.trim() || '这个角色还没有简介')
const isPublished = computed(() => props.profile.status === 11)
const authorName = computed(() => props.profile.user?.nickname?.trim() || '未知作者')
const stats = computed(() => [
  {
    value: formatNumber(props.profile.useNum ?? 0),
    label: '互动与播放',
  },
  {
    value: formatNumber(props.profile.fansNum ?? 0),
    label: '关注',
  },
])
const followLabel = computed(() => (props.following ? '已关注' : '关注'))
const avatarLoaded = shallowRef(false)

watch(
  () => avatarSrc.value,
  () => {
    avatarLoaded.value = false
  },
  { immediate: true },
)

function handleFollowClick() {
  emit('followClick')
}

function handleAuthorClick() {
  if (!props.profile.user?.uid) {
    return
  }

  emit('authorClick')
}

function handleAvatarLoad() {
  avatarLoaded.value = true
}
</script>

<template>
  <header class="cartoon-header">
    <div class="cartoon-header__avatar-shell">
      <img
        class="cartoon-header__avatar"
        :class="{ 'is-loaded': avatarLoaded }"
        :src="avatarSrc"
        :alt="profile.name"
        @load="handleAvatarLoad"
      />
    </div>

    <div class="cartoon-header__identity">
      <h1 class="cartoon-header__name">{{ profile.name }}</h1>
      <button
        v-if="profile.user?.uid"
        type="button"
        class="cartoon-header__author"
        @click="handleAuthorClick"
      >
        @{{ authorName }}
      </button>
      <p v-else class="cartoon-header__author is-static">@{{ authorName }}</p>
    </div>

    <div v-if="isPublished" class="cartoon-header__stats" aria-label="角色数据">
      <div v-for="stat in stats" :key="stat.label" class="cartoon-header__stat">
        <strong>{{ stat.value }}</strong>
        <span>{{ stat.label }}</span>
      </div>
    </div>

    <p v-else class="cartoon-header__draft-hint">角色未发布</p>

    <p class="cartoon-header__summary">{{ summaryText }}</p>

    <button
      v-if="showFollowButton && !isMine"
      type="button"
      class="cartoon-header__follow"
      :class="{ 'is-following': following }"
      :disabled="followLoading"
      @click="handleFollowClick"
    >
      {{ followLabel }}
    </button>
  </header>
</template>

<style scoped>
.cartoon-header {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 16px 24px;
  color: var(--profile-text);
  text-align: center;
}

.cartoon-header__avatar-shell,
.cartoon-header__avatar {
  width: 138px;
  height: 138px;
}

.cartoon-header__avatar-shell {
  overflow: hidden;
  border-radius: 50%;
  background: var(--app-skeleton-base);
  box-shadow: 0 12px 32px rgb(0 0 0 / 0.08);
}

.cartoon-header__avatar {
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.22s ease;
}

.cartoon-header__avatar.is-loaded {
  opacity: 1;
}

.cartoon-header__identity {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.cartoon-header__name {
  margin: 0;
  color: var(--profile-text);
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
}

.cartoon-header__author {
  color: var(--profile-muted);
  font-size: 14px;
  line-height: 20px;
  transition:
    color 0.18s ease,
    opacity 0.18s ease;
}

.cartoon-header__author:hover,
.cartoon-header__author:focus-visible {
  color: var(--profile-text);
}

.cartoon-header__author:active {
  opacity: 0.72;
}

.cartoon-header__author.is-static {
  margin: 0;
}

.cartoon-header__stats {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 24px;
}

.cartoon-header__stat {
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
  color: var(--profile-text);
  font-size: 14px;
  line-height: 20px;
}

.cartoon-header__stat strong {
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
}

.cartoon-header__draft-hint {
  margin: 0;
  color: var(--profile-muted);
  font-size: 14px;
  line-height: 20px;
}

.cartoon-header__summary {
  width: min(464px, 100%);
  margin: 0;
  color: var(--profile-text);
  font-size: 14px;
  line-height: 22px;
}

.cartoon-header__follow {
  min-width: 104px;
  border-radius: 999px;
  background: #06c755;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  padding: 8px 22px;
  transition:
    opacity 0.18s ease,
    background 0.18s ease,
    color 0.18s ease;
}

.cartoon-header__follow:hover:not(:disabled),
.cartoon-header__follow:focus-visible:not(:disabled) {
  opacity: 0.92;
}

.cartoon-header__follow:active:not(:disabled) {
  opacity: 0.86;
}

.cartoon-header__follow:disabled {
  cursor: wait;
  opacity: 0.72;
}

.cartoon-header__follow.is-following {
  background: var(--app-button-secondary-bg);
  color: var(--app-button-secondary-fg);
  box-shadow: inset 0 0 0 1px var(--app-border-subtle);
}

.cartoon-header__follow.is-following:hover:not(:disabled) {
  background: var(--app-button-secondary-hover);
}

@media (max-width: 640px) {
  .cartoon-header {
    gap: 14px;
    padding: 28px 16px 18px;
  }

  .cartoon-header__avatar-shell,
  .cartoon-header__avatar {
    width: 112px;
    height: 112px;
  }

  .cartoon-header__name {
    font-size: 22px;
    line-height: 30px;
  }

  .cartoon-header__stats {
    gap: 16px;
  }

  .cartoon-header__summary {
    width: min(360px, 100%);
    font-size: 13px;
    line-height: 20px;
  }
}
</style>
